import { BusinessDocumentsProvider } from '@/components/InvoiceReportDetailDrawer/ApplicationContent/context/BusinessDocumentsContext';
import { useViewApproveRouteDrawer } from '@/components/InvoiceReportDetailDrawer/ApprovalRouteDrawer';
import { DEFAULT_SECTION_STATES } from '@/components/InvoiceReportDetailDrawer/constants';
import { PaymentDetailProvider } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { FileInfoDrawerProvider } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/context/FileInfoDrawerContext';
import { useDrawerWidth } from '@/components/InvoiceReportDetailDrawer/hooks/useDrawerWidth';
import { useSectionCollapseStates } from '@/components/InvoiceReportDetailDrawer/hooks/useSectionCollapseStates';
import type { CheckPermissionFn } from '@/components/InvoiceReportDetailDrawer/type';
import { AdminInvoiceReportApprovalRouteDrawer } from '@/features/admin_invoice_reports/components/AdminInvoiceReportApprovalRouteDrawer';
import { AdminInvoiceReportDrawerHeaderActions } from '@/features/admin_invoice_reports/components/AdminInvoiceReportDetailDrawer/components/AdminInvoiceReportDrawerHeaderActions';
import { useConfirmCancelModal } from '@/features/admin_invoice_reports/components/ConfirmCancelModal';
import { useConfirmDeleteModal } from '@/features/admin_invoice_reports/components/ConfirmDeleteModal';
import { formatConfirmModalTotalAmount } from '@/features/admin_invoice_reports/utils/formatConfirmModalTotalAmount';
import { DraggableDrawer } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { BusinessDocumentDrawerContainer } from './BusinessDocumentDrawerContainer';
import { DrawerTitle } from './components/DrawerTitle';
import {
  DEFAULT_DRAWER_DETAIL_WIDTH,
  DRAWER_WIDTH_STORAGE_KEY,
} from './constants';
import { AdminInvoiceReportDetail } from './Detail';
import styles from './Drawer.module.scss';
import { useAdminInvoiceReportSummaryAccessErrorEffect } from './hooks/useAdminInvoiceReportSummaryAccessErrorEffect';
import { useAdminInvoiceReportSummaryData } from './hooks/useAdminInvoiceReportSummaryData';

const cx = classnames.bind(styles);

export type AdminInvoiceReportDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  selectedId: string;
  /** Re-fetch the underlying list query (NA-4869: 404 path refreshes the table). */
  onListRefetch: CheckPermissionFn;
};

// Must be rendered inside ConfirmCancelModalProvider (provided by AdminInvoiceReportsTable).
export const AdminInvoiceReportDetailDrawer: FC<AdminInvoiceReportDetailDrawerProps> =
  memo(({ open, onClose, selectedId, onListRefetch }) => {
    const { width, setWidth } = useDrawerWidth(
      DEFAULT_DRAWER_DETAIL_WIDTH,
      DRAWER_WIDTH_STORAGE_KEY
    );
    const { data, isLoading, error } =
      useAdminInvoiceReportSummaryData(selectedId);

    useAdminInvoiceReportSummaryAccessErrorEffect({
      error,
      onClose,
      onListRefetch,
    });

    const {
      isOpen: approvalRouteOpen,
      onDrawerOpen: onOpenApprovalRoute,
      onDrawerClose: onCloseApprovalRoute,
    } = useViewApproveRouteDrawer();

    const { states: sectionStates, onSectionToggle } = useSectionCollapseStates(
      DEFAULT_SECTION_STATES
    );

    const { onOpenConfirmDeleteModal } = useConfirmDeleteModal();
    const { onOpenConfirmCancelModal } = useConfirmCancelModal();

    const titleInfo = useMemo(
      () => ({ title: data?.title ?? '', number: data?.number ?? '' }),
      [data?.title, data?.number]
    );

    const handleCancel = useCallback(() => {
      onOpenConfirmCancelModal({
        id: selectedId,
        ...titleInfo,
        apPayeeName: data?.ap_payee_name ?? null,
        totalAmount: formatConfirmModalTotalAmount(data?.total_value, 'drawer'),
      });
    }, [
      onOpenConfirmCancelModal,
      selectedId,
      titleInfo,
      data?.ap_payee_name,
      data?.total_value,
    ]);

    const handleDelete = useCallback(() => {
      onOpenConfirmDeleteModal({
        id: selectedId,
        ...titleInfo,
        apPayeeName: data?.ap_payee_name,
        totalAmount: data?.total_value,
      });
    }, [
      onOpenConfirmDeleteModal,
      selectedId,
      titleInfo,
      data?.ap_payee_name,
      data?.total_value,
    ]);

    const headerActions = useMemo(() => {
      if (!data) return undefined;
      return (
        <AdminInvoiceReportDrawerHeaderActions
          detailPath={data.detail_path}
          editPath={data.edit_path}
          editAction={data.edit_action}
          cancelAction={data.cancel_action}
          deleteAction={data.delete_action}
          handleCancel={handleCancel}
          handleDelete={handleDelete}
        />
      );
    }, [data, handleCancel, handleDelete]);

    return (
      <PaymentDetailProvider>
        <FileInfoDrawerProvider>
          <BusinessDocumentsProvider>
            <DraggableDrawer
              visible={open}
              onClose={onClose}
              destroyOnClose
              title={
                <DrawerTitle
                  titleInfo={titleInfo}
                  hasApprovalRoute={data?.can_display_approval_route ?? false}
                  onOpenApprovalRoute={onOpenApprovalRoute}
                  headerActions={headerActions}
                />
              }
              contentContainerClassName={cx(styles['contentContainer'])}
              className={cx(styles['adminInvoiceReportDetailDrawer'])}
              width={width}
              setWidth={setWidth}
              minWidth={DEFAULT_DRAWER_DETAIL_WIDTH}
            >
              <AdminInvoiceReportDetail
                reportId={selectedId}
                summaryData={data}
                isLoading={isLoading}
                sectionStates={sectionStates}
                onSectionToggle={onSectionToggle}
              />
            </DraggableDrawer>
            {approvalRouteOpen && (
              <AdminInvoiceReportApprovalRouteDrawer
                open={approvalRouteOpen}
                reportId={selectedId}
                onCloseDetailDrawer={onClose}
                onClose={onCloseApprovalRoute}
              />
            )}
            <BusinessDocumentDrawerContainer onCloseDetailDrawer={onClose} />
          </BusinessDocumentsProvider>
        </FileInfoDrawerProvider>
      </PaymentDetailProvider>
    );
  });

AdminInvoiceReportDetailDrawer.displayName = 'AdminInvoiceReportDetailDrawer';
